// Extraction status types from API
export type ExtractionStatus = 'pending' | 'processing' | 'completed' | 'failed';

// Session data stored in browser storage
export interface OnboardingSession {
  sessionId: string;
  currentStep: number;
  expiresAt: string;
  stepData: OnboardingStepData;
}

// Step 1: Website URL + optional company name
export interface Step1Data {
  website_url: string;
  company_name?: string;
}

// Step 2: Target industries, job titles, countries
export interface Step2Data {
  target_industries: string[];
  target_job_titles: string[];
  target_countries: string[];
}

// Targeting sent alongside the completion request. Optional product_description lets a
// user's edit of the auto-extracted copy win over what the AI produced.
export interface OnboardingTargetingData extends Step2Data {
  product_description?: string;
}

// Aggregated step data
export interface OnboardingStepData {
  step1?: Step1Data;
  step2?: Step2Data;
  [key: string]: any;
}

// Product extraction result from API
export interface ProductExtractionData {
  product_description?: string;
  company_name?: string;
  benefits?: string[];
  pains?: string[];
  extraction_source?: string;
  errors?: string[];
}

// Lead generated via WebSocket
export interface OnboardingLead {
  id?: number;
  name?: string;
  email?: string;
  company?: string;
  job_title?: string;
  linkedin_url?: string;
  [key: string]: any;
}

// Complete onboarding response
export interface OnboardingCompleteResult {
  user: any;
  icp: any;
  workspace: any;
  token: string;
  lead_generation_status: string;
}

// NgRx State
export interface OnboardingState {
  session: {
    sessionId: string | null;
    currentStep: number;
    expiresAt: string | null;
    isLoading: boolean;
    error: string | null;
  };
  stepData: OnboardingStepData;
  extraction: {
    status: ExtractionStatus | null;
    extractionSource: string | null;
    productDescription: string | null;
    companyName: string | null;
    benefits: string[] | null;
    pains: string[] | null;
    isLoading: boolean;
    error: string | null;
  };
  updateStep: {
    isLoading: boolean;
    success: boolean;
    error: string | null;
  };
  complete: {
    isLoading: boolean;
    success: boolean;
    result: OnboardingCompleteResult | null;
    error: string | null;
  };
  leads: {
    items: OnboardingLead[];
    isListening: boolean;
    generationStatus: string | null;
  };
}

export const initialOnboardingState: OnboardingState = {
  session: {
    sessionId: null,
    currentStep: 1,
    expiresAt: null,
    isLoading: false,
    error: null
  },
  stepData: {},
  extraction: {
    status: null,
    extractionSource: null,
    productDescription: null,
    companyName: null,
    benefits: null,
    pains: null,
    isLoading: false,
    error: null
  },
  updateStep: {
    isLoading: false,
    success: false,
    error: null
  },
  complete: {
    isLoading: false,
    success: false,
    result: null,
    error: null
  },
  leads: {
    items: [],
    isListening: false,
    generationStatus: null
  }
};
